import { z } from "zod";

export const TransactionTypeSchema = z.enum([
  "send_money",
  "earnings",
  "withdrawal",
  "deposit",
  "spend",
  "refund",
]);

export const TransactionStatusSchema = z.enum([
  "processing",
  "successful",
  "failed",
  "cancelled",
]);

export const ExchangeRateSchema = z.object({
  base_currency: z.string(),
  target_currency: z.string(),
  rate: z.number(),
});

export const ExchangeRateResponseSchema = z.object({
  message: z.string(),
  data: ExchangeRateSchema,
});

export const KycSchema = z.object({
  id: z.number(),
  kyc_id: z.string(),
  user_id: z.string(),
  current_tier: z.enum(["basic", "standard", "platinum"]),
  status: z.enum([
    "pending",
    "approved",
    "rejected",
    "under_review",
    "expired",
  ]),
  country: z.enum(["Nigeria", "Ghana"]),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const KycDetailsResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    user_id: z.string(),
    current_tier: z.enum(["basic", "standard", "platinum"]),
    status: z.string(),
    country: z.string(),
    currency_limits: z.record(z.string(), z.number()),
  }),
});

// Wallet Schema
export const WalletSchema = z.object({
  created_by: z.string(),
  user_id: z.string(),
  wallet_id: z.string(),
  type: z.enum(["managed", "premium"]),
  currency: z.string().default("CNY"),
  main_balance: z.coerce.number().optional().default(0),
  available_balance: z.coerce.number().optional().default(0),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  edges: z.object({
    kyc: KycSchema,
  }),
});

export const WalletResponseSchema = z.object({
  message: z.string(),
  data: WalletSchema,
});

// Withdrawal Schema
export const WithdrawalSchema = z.object({
  id: z.number(),
  withdrawal_id: z.string(),
  amount: z.number(),
  currency: z.string(),
  exchange_rate: z.number().optional(),
  account_name: z.string(),
  account_number: z.string(),
  bank_name: z.string(),
  bank_code: z.string().optional(),
  bank_slug: z.string().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

// Sendmoney Bank Schema
export const SendmoneyBankSchema = z.object({
  bank_country: z.string(),
  bank_name: z.string(),
  account_number: z.string(),
  account_name: z.string(),
});

// Sendmoney Alipay Schema
export const SendmoneyAlipaySchema = z.object({
  alipay_name: z.string(),
  alipay_email: z.string().optional(),
  alipay_phone: z.string().optional(),
  alipay_qr_code: z.string().optional(),
});

export const SendmoneyEdgesSchema = z.object({
  alipay_details: SendmoneyAlipaySchema.optional(),
  bank_details: SendmoneyBankSchema.optional(),
});

// Sendmoney Schema
export const SendmoneySchema = z.object({
  id: z.number(),
  sendmoney_id: z.string(),
  purpose: z.enum(["supplier", "personal"]),
  amount: z.number(),
  currency: z.string(),
  exchange_rate: z.number().optional(),
  description: z.string(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  edges: SendmoneyEdgesSchema,
});

// Deposit Schema
export const DepositSchema = z.object({
  id: z.number(),
  deposit_id: z.string(),
  amount: z.number(),
  currency: z.string(),
  exchange_rate: z.number().optional(),
  payment_provider: z.string(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  edges: z.object({}).optional(),
});

// Transaction Edges Schema
export const TransactionEdgesSchema = z.object({
  deposit: DepositSchema.optional(),
  withdrawal: WithdrawalSchema.optional(),
  sendmoney: SendmoneySchema.optional(),
});

export const TransactionsSchema = z.object({
  id: z.number(),
  user_id: z.string(),
  initiator_id: z.string(),
  transaction_id: z.string(),
  transaction_type: TransactionTypeSchema.optional(),
  transaction_category: z.string(),
  amount: z.number(),
  status: TransactionStatusSchema,
  currency: z.string(),
  description: z.string().optional(),
  charge: z.number().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  edges: TransactionEdgesSchema.optional(),
});

export const TransactionsListResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    transactions: z.array(TransactionsSchema),
    pagination: z.object({
      current_page: z.number(),
      size: z.number(),
      total_pages: z.number(),
      total_records: z.number(),
    }),
  }),
});

export const TransactionSchema = z.object({
  id: z.number(),
  user_id: z.string(),
  initiator_id: z.string(),
  transaction_id: z.string(),
  transaction_type: TransactionTypeSchema,
  transaction_category: z.string(),
  amount: z.number(),
  status: TransactionStatusSchema,
  currency: z.string(),
  description: z.string().optional(),
  charge: z.number().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  edges: TransactionEdgesSchema,
});

export const TransactionResponseSchema = z.object({
  message: z.string(),
  data: TransactionSchema,
});

export const TransactionListResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    pagination: z.object({
      current_page: z.number(),
      size: z.number(),
      total_pages: z.number(),
      total_records: z.number(),
    }),
    transactions: z.array(TransactionSchema),
  }),
});

// Resolve Account Request Schema
export const ResolveAccountRequestSchema = z.object({
  institution_code: z.string(),
  identifier: z.string(),
  country: z.string(),
});

export const ResolveAccountResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    nigeria: z
      .object({
        bank_name: z.string(),
        bank_code: z.string(),
        account_number: z.string(),
        account_name: z.string(),
      })
      .optional(),
    ghana: z
      .object({
        bank_name: z.string(),
        bank_code: z.string(),
        account_number: z.string(),
        account_name: z.string(),
      })
      .optional(),
  }),
});

// Deposit Request Schema
export const DepositRequestSchema = z.object({
  payment_provider: z.string(),
  wallet_id: z.string(),
  amount: z.number(),
  user_id: z.string(),
  currency: z.string(),
});

// Beneficiary List Schema
const BaseBeneficiarySchema = z.object({
  id: z.number().optional(),
  beneficiary_id: z.string(),
  user_id: z.string(),
  method: z.enum(["bank", "alipay"]),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});

// Bank beneficiary schema
const BankBeneficiarySchema = BaseBeneficiarySchema.extend({
  method: z.literal("bank"),
  country: z.string().optional(),
  bank_country: z.string().optional(),
  account_name: z.string(),
  account_number: z.string(),
  bank_name: z.string(),
  bank_code: z.string().optional(),
  bank_slug: z.string().optional(),
});

// Alipay beneficiary schema
const AlipayBeneficiarySchema = BaseBeneficiarySchema.extend({
  method: z.literal("alipay"),
  alipay_name: z.string(),
  alipay_qr_code: z.string().optional(),
  alipay_email: z.string().optional(),
  alipay_phone: z.string().optional(),
});

export const BeneficiarySchema = z.discriminatedUnion("method", [
  BankBeneficiarySchema,
  AlipayBeneficiarySchema,
]);

export const PaginationSchema = z.object({
  current_page: z.number(),
  size: z.number(),
  total_pages: z.number(),
  total_records: z.number(),
});

export const BeneficiaryListResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    data: z.array(BeneficiarySchema),
    pagination: PaginationSchema,
  }),
  meta: z.object({
    userId: z.string(),
  }).optional(),
});

// Withdrawal Beneficiary Schema
export const WithdrawalBeneficiarySchema = z.object({
  ID: z.number(),
  beneficiary_id: z.string(),
  user_id: z.string(),
  country: z.string(),
  account_name: z.string(),
  account_number: z.string(),
  bank_name: z.string(),
  bank_code: z.string().optional(),
  bank_slug: z.string().optional(),
  deleted: z.boolean().default(false),
});

// Sendmoney Beneficiary Schema
export const SendmoneyBeneficiarySchema = z.object({
  beneficiary_id: z.string(),
  user_id: z.string(),
  method: z.enum(["bank", "alipay"]),
  bank_country: z.string().optional(),
  bank_name: z.string().optional(),
  account_number: z.string().optional(),
  account_name: z.string().optional(),
  alipay_name: z.string().optional(),
  alipay_email: z.string().optional(),
  alipay_phone: z.string().optional(),
  alipay_qr_code: z.string().optional(),
  deleted: z.boolean().default(false),
});

export const SendMoneyBeneficiaryResponseSchema = z.object({
  message: z.string(),
  data: SendmoneyBeneficiarySchema,
  meta: z.optional(z.any()),
});

export const CreateDepositResponse = z.object({
  message: z.string(),
  data: z.object({
    wallet_id: z.string(),
    amount: z.number(),
    user_id: z.string(),
    currency: z.string(),
    transaction_id: z.string(),
    deposit_id: z.string(),
  }),
});

const BankSchema = z.object({
  name: z.string(),
  slug: z.string(),
  code: z.string(),
  country: z.string(),
});

const BankListDataSchema = z.object({
  nigeria: z.array(BankSchema).optional(),
  ghana: z.array(BankSchema).optional(),
});

// Main response schema
export const BankListResponseSchema = z.object({
  message: z.string(),
  data: BankListDataSchema,
});

export const StatsSchema = z.object({
  send_money: z.number().optional(),
  earnings: z.number().optional(),
  withdrawal: z.number().optional(),
  deposit: z.number().optional(),
  spend: z.number().optional(),
  refund: z.number().optional(),
});

export const StatsResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    transaction: StatsSchema,
  }),
});

export const FeeCalculationResponseSchema = z.object({
  amount: z.number(),
  amount_currency: z.string(),
  charge: z.number(),
  charge_currency: z.string(),
  charge_name: z.string(),
  total: z.number(),
  total_currency: z.string(),
  exchange_rate: z.number().optional(),
});

// TypeScript types derived from the schemas
export type BeneficiaryListResponse = z.infer<
  typeof BeneficiaryListResponseSchema
>;
export type Exchangerate = z.infer<typeof ExchangeRateSchema>;
export type ExchangeRateResponse = z.infer<typeof ExchangeRateResponseSchema>;
export type Wallet = z.infer<typeof WalletSchema>;
export type WalletResponse = z.infer<typeof WalletResponseSchema>;
export type TransactionStatus = z.infer<typeof TransactionStatusSchema>;
export type TransactionType = z.infer<typeof TransactionTypeSchema>;
export type Transaction = z.infer<typeof TransactionSchema>;
export type TransactionListResponse = z.infer<
  typeof TransactionListResponseSchema
>;
export type Transactions = z.infer<typeof TransactionsSchema>;
export type TransactionsListResponse = z.infer<
  typeof TransactionsListResponseSchema
>;
export type BankListResponse = z.infer<typeof BankListResponseSchema>;
export type ResolveAccountRequest = z.infer<typeof ResolveAccountRequestSchema>;
export type ResolveAccountResponse = z.infer<
  typeof ResolveAccountResponseSchema
>;
export type Beneficiary = z.infer<typeof BeneficiarySchema>;
export type WithdrawalBeneficiary = z.infer<typeof WithdrawalBeneficiarySchema>;
export type SendmoneyBeneficiary = z.infer<typeof SendmoneyBeneficiarySchema>;
export type SendMoneyBeneficiaryResponse = z.infer<
  typeof SendMoneyBeneficiaryResponseSchema
>;
export type StatsResponse = z.infer<typeof StatsResponseSchema>;
export type FeeCalculationResponse = z.infer<typeof FeeCalculationResponseSchema>;

// Constants for validation
export const TransactionStatusMap = {
  processing: 0,
  successful: 1,
  failed: 2,
  cancelled: 3,
} as const;

export const AdminUpdateWalletBalanceActionsMap = {
  credit: 0,
  debit: 1,
} as const;

export type KycDetailsResponse = z.infer<typeof KycDetailsResponseSchema>;
